// Home's quick catches: what needs the person now, in one short list, each a tap away. Worked out on the device from
// what Home already has (the company's records, the latest changes, the inbox, the weather and, for the VERANeural team,
// HQ's alerts). Simple, explainable rules; nothing is invented and nothing is sent anywhere.

import { useMemo } from "react";
import { Icon, useStudio } from "./ui";
import { T } from "../lib/i18n";
import { CLOSED, type CrmChange, type CrmRecord, type CrmType } from "../lib/crm";
import { useGraph, type Mail } from "../lib/m365";
import { devMode } from "../auth/auth";
import { useHomePrefs } from "../lib/homePrefs";
import { useWeather, weatherWarning } from "../lib/weather";
import { useAlerts } from "../lib/alerts";
import { vera } from "../api/vera";

type Item = { key: string; icon: string; text: string; tone?: "warn"; go: () => void };

const SEEN = "vera-home-changes-seen";
const seenAt = () => { try { return Number(localStorage.getItem(SEEN) ?? 0) || 0; } catch { return 0; } };
export const markChangesSeen = () => { try { localStorage.setItem(SEEN, String(Date.now())); } catch { /* ignore */ } };

const today = () => new Date().toISOString().slice(0, 10);
const emailsIn = (r: CrmRecord) => Object.values(r.values).filter((v): v is string => typeof v === "string" && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim())).map((v) => v.trim().toLowerCase());

export function Notifications({ jobsType, dateField, lists, types, changes }: {
  jobsType?: CrmType; dateField?: string; lists: Record<string, CrmRecord[]>; types: CrmType[]; changes: CrmChange[] | null;
}) {
  const { go, user, m365, profile } = useStudio();
  const { prefs } = useHomePrefs();
  const live = vera.mode === "live";
  const outlook = m365.mail();
  const mail = useGraph<Mail[]>("mail.inbox", !devMode && outlook && live);
  const weather = useWeather(prefs.place, live);
  const team = !profile.external && live;
  const { unread: alerts } = useAlerts(team);

  const items = useMemo(() => {
    const out: Item[] = [];
    if (team && alerts > 0) out.push({ key: "alerts", icon: "history", text: `${alerts} ${alerts === 1 ? T("new alert") : T("new alerts")} ${T("in Activity")}`, go: () => go("activity") });

    const warn = weatherWarning(weather?.weather ?? null);
    if (warn) out.push({ key: "weather", icon: "sun", text: T(warn), tone: "warn", go: () => go("workspace", { ask: T("What does the weather mean for today's work on the water?"), title: T("Weather") }) });

    if (jobsType && dateField) {
      const status = jobsType.statusField;
      const open = (lists[jobsType.id] ?? []).filter((j) => !status || !CLOSED.test(String(j.values[status] ?? "")));
      const d = today();
      const late = open.filter((j) => typeof j.values[dateField] === "string" && (j.values[dateField] as string).slice(0, 10) < d);
      const now = open.filter((j) => typeof j.values[dateField] === "string" && (j.values[dateField] as string).slice(0, 10) === d);
      const plural = jobsType.plural.toLowerCase();
      if (late.length) out.push({ key: "late", icon: "cal", tone: "warn", text: `${late.length} ${plural} ${T("past their date")}`, go: () => go("records", { crmType: jobsType.id }) });
      if (now.length) out.push({ key: "today", icon: "cal", text: `${now.length} ${plural} ${T("for today")}`, go: () => go("records", { crmType: jobsType.id }) });
    }

    const orders = types.find((t) => t.id === "orders");
    if (orders) {
      const st = orders.statusField;
      const waiting = (lists.orders ?? []).filter((o) => !st || !CLOSED.test(String(o.values[st] ?? "")));
      if (waiting.length) out.push({ key: "orders", icon: "board", text: `${waiting.length} ${orders.plural.toLowerCase()} ${T("still open")}`, go: () => go("records", { crmType: "orders" }) });
    }

    // Emails from the company's own clients (an address on a client's record), still unread.
    const clientType = types.find((t) => t.id === "clients");
    if (clientType && mail.data) {
      const known = new Set((lists.clients ?? []).flatMap(emailsIn));
      const fromClients = mail.data.filter((m) => m.unread && known.has(m.from.email.toLowerCase()));
      if (fromClients.length) {
        const first = fromClients[0];
        out.push({ key: "mail", icon: "mail", text: fromClients.length === 1 ? `${T("Email from")} ${first.from.name || first.from.email}: ${first.subject}` : `${fromClients.length} ${T("unread emails from your clients")}`,
          go: () => go("workspace", { ask: fromClients.length === 1 ? `${T("Summarise the email from")} ${first.from.name || first.from.email} ${T("about")} "${first.subject}" ${T("and tell me what they need.")}` : T("Summarise the unread emails from my clients and what each one needs."), title: T("Client emails") }) });
      }
    }

    const since = seenAt();
    const byOthers = (changes ?? []).filter((c) => c.at > since && c.action !== "undo" && c.who !== user.name);
    if (byOthers.length) out.push({ key: "changes", icon: "history", text: `${byOthers.length} ${byOthers.length === 1 ? T("change by your team or VERA") : T("changes by your team or VERA")} ${T("since you last looked")}`, go: () => { markChangesSeen(); go("activity"); } });
    return out;
  }, [team, alerts, weather, jobsType, dateField, lists, types, mail.data, changes, user.name, go]);

  return (
    <section className="card">
      <div className="row between" style={{ marginBottom: 6 }}><h2>{T("Notifications")}</h2></div>
      {!items.length ? <p className="faint">{T("Nothing needs you right now.")}</p> : (
        <div className="list">{items.map((i) => (
          <button key={i.key} className={`li rowbtn notif${i.tone ? ` ${i.tone}` : ""}`} onClick={i.go}>
            <Icon name={i.icon} size={16} /><span className="grow" style={{ textAlign: "left" }}>{i.text}</span><Icon name="expand" size={13} />
          </button>))}
        </div>)}
    </section>
  );
}
